import React, { useState, useEffect } from 'react';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import { SuperAdminService } from '@/services/superadmin.service';
import { toast, Toaster } from 'react-hot-toast';
import { Save, Plus, Edit, Trash2, Check, X, ShieldAlert, ShieldCheck, Laptop, Smartphone, Globe, Monitor, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoImg from '@/assets/Sthitih_Logo.png';
import { NotificationBell } from '@/components/NotificationBell';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';

interface PlatformSettings {
  platformTitle: string;
  supportEmail: string;
  contactPhone: string;
  defaultTrialDurationDays: number;
  allowPublicSignups: boolean;
  maintenanceMode: boolean;
  taxPercentage: number;
}

interface Plan {
  id: string;
  name: string;
  price: number;
  storage_limit: number;
  api_requests_limit: number;
  email_quota_limit: number;
  features: string; // JSON string
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [resultModal, setResultModal] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [superAdminNotifications, setSuperAdminNotifications] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem('sthitih_superadmin_notifications_settings') || '{"newTenants":true,"supportTickets":true,"billingMismatches":false}');
    } catch (e) {
      return { newTenants: true, supportTickets: true, billingMismatches: false };
    }
  });

  const toggleSuperSetting = (key: string) => {
    const updated = {
      ...superAdminNotifications,
      [key]: !superAdminNotifications[key]
    };
    setSuperAdminNotifications(updated);
    localStorage.setItem('sthitih_superadmin_notifications_settings', JSON.stringify(updated));
    toast.success('Notification preferences updated');
  };

  // Plan modal states
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    price: 0,
    storageGB: 5,
    apiLimit: 50000,
    emailLimit: 5000,
    featuresText: '',
  });

  // Active sessions state
  const [activeSessionsList, setActiveSessionsList] = useState<any[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const fetchSessions = async () => {
    try {
      setSessionsLoading(true);
      const list = await SuperAdminService.getActiveSessions();
      setActiveSessionsList(list || []);
    } catch {
      setActiveSessionsList([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      fetchSessions();
      const [settingsData, plansData] = await Promise.all([
        SuperAdminService.getSettings(),
        SuperAdminService.getPlans()
      ]);
      setSettings(settingsData);
      setPlans(plansData);
    } catch {
      toast.error('Failed to load platform configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    if (!settings) return;
    try {
      setSavingSettings(true);
      await SuperAdminService.updateSettings(settings);
      setResultModal({ type: 'success', message: 'Platform configurations updated successfully' });
    } catch {
      setResultModal({ type: 'error', message: 'Failed to update configurations' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleOpenPlanModal = (plan?: Plan) => {
    if (plan) {
      setEditingPlan(plan);
      let parsedFeatures = '';
      try {
        const arr = JSON.parse(plan.features);
        if (Array.isArray(arr)) {
          parsedFeatures = arr.join(', ');
        }
      } catch {
        parsedFeatures = plan.features || '';
      }
      setPlanForm({
        name: plan.name,
        price: Number(plan.price),
        storageGB: Math.round(plan.storage_limit / 1073741824),
        apiLimit: plan.api_requests_limit,
        emailLimit: plan.email_quota_limit,
        featuresText: parsedFeatures,
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        name: '',
        price: 199,
        storageGB: 5,
        apiLimit: 50000,
        emailLimit: 5000,
        featuresText: 'Event Hosting, Team Support',
      });
    }
    setShowPlanModal(true);
  };

  const handleSavePlan = async () => {
    try {
      if (!planForm.name) {
        toast.error('Plan name is required');
        return;
      }

      const featuresArr = planForm.featuresText
        .split(',')
        .map(f => f.trim())
        .filter(Boolean);

      const payload = {
        name: planForm.name,
        price: planForm.price,
        storage_limit: planForm.storageGB * 1073741824, // to bytes
        api_requests_limit: planForm.apiLimit,
        email_quota_limit: planForm.emailLimit,
        features: JSON.stringify(featuresArr),
      };

      if (editingPlan) {
        await SuperAdminService.updatePlan(editingPlan.id, payload);
        setResultModal({ type: 'success', message: 'Subscription plan updated successfully' });
      } else {
        await SuperAdminService.createPlan(payload);
        setResultModal({ type: 'success', message: 'Subscription plan created successfully' });
      }
      setShowPlanModal(false);
      fetchData();
    } catch {
      setResultModal({ type: 'error', message: 'Failed to save subscription plan' });
    }
  };

  const handleDeletePlan = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this subscription plan? Clients associated with it will fallback.')) {
      return;
    }
    try {
      await SuperAdminService.deletePlan(id);
      setResultModal({ type: 'success', message: 'Subscription plan deleted successfully' });
      fetchData();
    } catch {
      setResultModal({ type: 'error', message: 'Failed to delete plan' });
    }
  };

  const updateSetting = (key: keyof PlatformSettings, value: any) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  const parseFeatures = (jsonStr: string) => {
    try {
      const arr = JSON.parse(jsonStr);
      if (Array.isArray(arr)) return arr;
      return [];
    } catch {
      return [];
    }
  };

  const inputClass = "w-full px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none bg-white text-xs text-[#243238] font-semibold";
  const labelClass = "block text-xs font-semibold text-[#4A5558] mb-1.5";

  return (
    <div className="flex min-h-screen bg-[#F7F2EA] text-[#243238]">
      <Toaster position="top-right" />
      <SuperAdminSidebar />

      <div className="flex-1 flex flex-col pl-0 pb-24 md:pl-20 md:pb-8 min-h-screen min-w-0 w-full relative">
        <SuperAdminMobileHeader title="Platform Settings" />

        <header className="hidden md:flex h-16 px-8 bg-[#F7F2EA] border-b border-[#DDD0BE] shadow-xs items-center justify-between sticky top-0 z-20 w-full">
          <h1 className="text-sm sm:text-base font-extrabold tracking-tight text-[#243238] uppercase">Platform Settings</h1>
          <div className="flex items-center gap-4 select-none">
            <NotificationBell type="superadmin" />
            <img src={logoImg} alt="Sthitih Logo" className="h-10 w-auto object-contain rounded-md shadow-xs border border-black/5" />
          </div>
        </header>

        <main className="flex-grow p-4 sm:p-8 max-w-5xl mx-auto w-full">
          {loading || !settings ? (
            <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#1F5A43] border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="space-y-8">
              
              {/* Platform Settings Details */}
              <section className="rounded-[18px] border border-[#E4D8C8] shadow-earth p-6 bg-[#FFFDF9]">
                <h3 className="text-xs font-bold text-[#243238] uppercase tracking-wider mb-5 flex items-center gap-1.5 pb-3 border-b border-[#E4D8C8]">
                  <ShieldAlert className="w-4 h-4 text-[#1F5A43]" />
                  <span>General Configurations</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Platform Title</label>
                    <input type="text" value={settings.platformTitle} onChange={e => updateSetting('platformTitle', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Support Email</label>
                    <input type="email" value={settings.supportEmail} onChange={e => updateSetting('supportEmail', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Contact Phone</label>
                    <input type="text" value={settings.contactPhone} onChange={e => updateSetting('contactPhone', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Default Trial Period (Days)</label>
                    <input type="number" value={settings.defaultTrialDurationDays} onChange={e => updateSetting('defaultTrialDurationDays', Number(e.target.value))} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Global Tax Rate (%)</label>
                    <input type="number" value={settings.taxPercentage} onChange={e => updateSetting('taxPercentage', parseFloat(e.target.value))} className={inputClass} />
                  </div>
                  
                  <div className="flex flex-col gap-4 mt-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={settings.allowPublicSignups} 
                        onChange={e => updateSetting('allowPublicSignups', e.target.checked)} 
                        className="w-4 h-4 text-[#1F5A43] border-[#DCCFBE] rounded focus:ring-[#1F5A43] accent-[#1F5A43]" 
                      />
                      <span className="text-xs font-semibold text-[#243238]">Enable Public Client Signups</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={settings.maintenanceMode} 
                        onChange={e => updateSetting('maintenanceMode', e.target.checked)} 
                        className="w-4 h-4 text-[#D64545] border-[#DCCFBE] rounded focus:ring-[#D64545] accent-[#D64545]" 
                      />
                      <span className="text-xs font-semibold text-[#D64545]">Activate System Maintenance Mode</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end mt-6 pt-4 border-t border-[#E4D8C8]">
                  <button onClick={handleSaveSettings} disabled={savingSettings}
                    className="btn-primary text-xs flex items-center gap-2">
                    <Save className="w-4 h-4" /> {savingSettings ? 'Saving...' : 'Save General Config'}
                  </button>
                </div>
              </section>

              {/* Plans Manager Section */}
              <section className="rounded-[18px] border border-[#E4D8C8] shadow-earth p-6 bg-[#FFFDF9]">
                <div className="flex justify-between items-center mb-6 pb-3 border-b border-[#E4D8C8]">
                  <h3 className="text-xs font-bold text-[#243238] uppercase tracking-wider flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-[#8A4B22]" />
                    <span>Manage Pricing & Subscription Plans</span>
                  </h3>
                  <button 
                    onClick={() => handleOpenPlanModal()}
                    className="btn-primary text-[10px] flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Custom Plan
                  </button>
                </div>

                {/* Plan Cards List */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {plans.map((p) => (
                    <div 
                      key={p.id}
                      className="rounded-[18px] border border-[#E4D8C8] p-5 bg-[#F7F2EA]/40 hover:border-[#1F5A43] transition-all flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-bold text-sm text-[#243238] capitalize">{p.name}</h4>
                          <span className="text-xs font-bold text-[#1F5A43]">₹{parseFloat(p.price as any).toLocaleString()} / mo</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 my-4 text-[10px] text-[#6F706C] font-semibold bg-[#FFFDF9] p-3 rounded-xl border border-[#E4D8C8]">
                          <div>
                            <span className="block text-[#8D8A83]">STORAGE</span>
                            <span className="text-[#243238] font-bold">{Math.round(p.storage_limit / 1073741824)} GB</span>
                          </div>
                          <div>
                            <span className="block text-[#8D8A83]">API QUOTA</span>
                            <span className="text-[#243238] font-bold">{p.api_requests_limit.toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="block text-[#8D8A83]">EMAILS</span>
                            <span className="text-[#243238] font-bold">{p.email_quota_limit.toLocaleString()}</span>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wide">Included Modules</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {parseFeatures(p.features).map((feat, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full bg-[#DFF5E7] text-[#1F7A4F] text-[9px] font-bold border border-[#1F7A4F]/20">
                                {feat}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 mt-6 pt-3 border-t border-[#DDD0BE]/60">
                        <button 
                          onClick={() => handleOpenPlanModal(p)}
                          className="btn-secondary text-[10px] flex items-center gap-1 px-2.5 py-1"
                        >
                          <Edit className="w-3 h-3" /> Edit
                        </button>
                        {p.id !== 'basic' && p.id !== 'professional' && p.id !== 'enterprise' && (
                          <button 
                            onClick={() => handleDeletePlan(p.id)}
                            className="btn-destructive text-[10px] flex items-center gap-1 px-2.5 py-1"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Notification Preferences Section */}
              <section className="rounded-[18px] border border-[#E4D8C8] shadow-earth p-6 bg-[#FFFDF9] space-y-4">
                <h3 className="text-xs font-bold text-[#243238] uppercase tracking-wider flex items-center gap-1.5 border-b border-[#E4D8C8] pb-3">
                  <Bell className="w-4 h-4 text-[#1F5A43]" />
                  <span>Super Admin Notification Settings</span>
                </h3>
                <p className="text-[11px] text-[#6F706C] font-semibold">Configure which system alerts you want to dispatch or receive by email copy.</p>

                <div className="space-y-4 pt-2">
                  {[
                    { key: 'newTenants', title: 'New Organization Subscriptions', desc: 'Notify by email when a new client admin signs up or pays subscription invoice.' },
                    { key: 'supportTickets', title: 'Urgent Support Tickets Raised', desc: 'Trigger immediate email alerts when high-priority support logs are created.' },
                    { key: 'billingMismatches', title: 'Payment Failures & Refund Logs', desc: 'Send notification digests when payment methods are rejected or manual refund logs occur.' }
                  ].map((item) => {
                    const isChecked = superAdminNotifications[item.key] || false;
                    return (
                      <div key={item.key} className="flex items-start justify-between p-3.5 bg-[#F7F2EA] border border-[#DDD0BE]/60 rounded-xl hover:bg-[#EFE5D6]/40 transition-colors">
                        <div className="space-y-0.5">
                          <h4 className="text-xs font-bold text-[#243238]">{item.title}</h4>
                          <p className="text-[10px] text-[#6F706C] leading-relaxed font-semibold">{item.desc}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSuperSetting(item.key)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-[#DDD0BE] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#DDD0BE] after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#1F5A43]"></div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Active Login Sessions & Device Management Section */}
              <section className="rounded-[18px] border border-[#E4D8C8] shadow-earth p-6 bg-[#FFFDF9] space-y-4">
                <div className="flex items-center justify-between border-b border-[#E4D8C8] pb-3">
                  <h3 className="text-xs font-bold text-[#243238] uppercase tracking-wider flex items-center gap-1.5">
                    <ShieldAlert className="w-4 h-4 text-[#993300]" />
                    <span>Active Login Sessions & Security Control</span>
                  </h3>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Revoke all other logged-in sessions across all devices?')) return;
                      try {
                        await SuperAdminService.revokeOtherSessions();
                        toast.success('Terminated all other active sessions!');
                        fetchSessions();
                      } catch {
                        toast.error('Failed to revoke sessions');
                      }
                    }}
                    className="btn-secondary text-[10px] font-bold text-rose-700 bg-rose-50 border-rose-200 hover:bg-rose-100 px-3 py-1.5 rounded-xl cursor-pointer"
                  >
                    Revoke All Other Sessions
                  </button>
                </div>
                <p className="text-[11px] text-[#6F706C] font-semibold">Monitor all active devices and locations logged into Super Admin account. Terminate any unrecognized sessions immediately.</p>

                <div className="space-y-3 pt-2">
                  {sessionsLoading ? (
                    <div className="p-6 text-center text-xs text-[#6F706C] flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-[#1F5A43] border-t-transparent rounded-full animate-spin" />
                      <span>Tracking active device sessions...</span>
                    </div>
                  ) : activeSessionsList.length === 0 ? (
                    <div className="p-6 text-center text-xs text-[#8D8A83] italic border border-dashed border-[#DDD0BE] rounded-2xl">
                      No active sessions logged yet.
                    </div>
                  ) : (
                    activeSessionsList.map((sess: any) => {
                      const isMobile = (sess.device_type || '').toLowerCase().includes('mobile') || (sess.device_type || '').toLowerCase().includes('phone');
                      const DeviceIcon = isMobile ? Smartphone : Monitor;
                      return (
                        <div key={sess.id} className="p-4 bg-[#F7F2EA] border border-[#DDD0BE]/80 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
                          <div className="flex items-start sm:items-center gap-3.5">
                            <div className="p-3 rounded-2xl bg-white border border-[#E4D8C8] text-[#1F5A43] shrink-0 shadow-xs">
                              <DeviceIcon className="w-5 h-5 text-[#1F5A43]" />
                            </div>
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-bold text-[#243238]">{sess.device_type || 'Desktop PC (Web Browser)'}</span>
                                {sess.is_current ? (
                                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300 font-extrabold text-[9px] uppercase tracking-wider">
                                    Current Session
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-bold text-[9px] uppercase">
                                    Other Device
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-[#6F706C] font-semibold flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                                <span>IP Address: <strong className="font-mono text-[#8A4B22]">{sess.ip_address || '127.0.0.1'}</strong></span>
                                <span>•</span>
                                <span>Logged in: {new Date(sess.created_at || Date.now()).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
                                <span>•</span>
                                <span>Last Active: {new Date(sess.last_active_at || sess.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                            {!sess.is_current && (
                              <button
                                onClick={async () => {
                                  try {
                                    await SuperAdminService.revokeSession(sess.id);
                                    toast.success('Terminated device session!');
                                    fetchSessions();
                                  } catch {
                                    toast.error('Failed to revoke session');
                                  }
                                }}
                                className="px-3.5 py-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-xs font-bold rounded-xl cursor-pointer transition-colors shadow-xs"
                              >
                                Revoke Session
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            </div>
          )}
        </main>
      </div>

      {/* Plan Creator/Editor Modal */}
      <AnimatePresence>
        {showPlanModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000]" onClick={() => setShowPlanModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFDF9] rounded-[18px] border border-[#E4D8C8] shadow-earth p-8 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
              <h2 className="text-sm font-bold text-[#243238] uppercase tracking-wider mb-6">
                {editingPlan ? 'Edit Subscription Plan' : 'Create Custom Plan'}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Plan Name</label>
                  <input 
                    type="text" 
                    value={planForm.name} 
                    onChange={e => setPlanForm({ ...planForm, name: e.target.value })} 
                    placeholder="e.g. Ultra Pro Plan" 
                    className={inputClass} 
                  />
                </div>
                <div>
                  <label className={labelClass}>Monthly Price (₹)</label>
                  <input 
                    type="number" 
                    value={planForm.price} 
                    onChange={e => setPlanForm({ ...planForm, price: Number(e.target.value) })} 
                    className={inputClass} 
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>Storage (GB)</label>
                    <input 
                      type="number" 
                      value={planForm.storageGB} 
                      onChange={e => setPlanForm({ ...planForm, storageGB: Number(e.target.value) })} 
                      className={inputClass} 
                    />
                  </div>
                  <div>
                    <label className={labelClass}>API Limit</label>
                    <input 
                      type="number" 
                      value={planForm.apiLimit} 
                      onChange={e => setPlanForm({ ...planForm, apiLimit: Number(e.target.value) })} 
                      className={inputClass} 
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Email Quota</label>
                    <input 
                      type="number" 
                      value={planForm.emailLimit} 
                      onChange={e => setPlanForm({ ...planForm, emailLimit: Number(e.target.value) })} 
                      className={inputClass} 
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Included Modules (Comma separated)</label>
                  <input 
                    type="text" 
                    value={planForm.featuresText} 
                    onChange={e => setPlanForm({ ...planForm, featuresText: e.target.value })} 
                    placeholder="e.g. SLA Support, Private Registry" 
                    className={inputClass} 
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button onClick={() => setShowPlanModal(false)} className="flex-1 btn-secondary text-xs">Cancel</button>
                <button onClick={handleSavePlan} className="flex-1 btn-primary text-xs">Save Plan</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result Confirmation Box */}
      <AnimatePresence>
        {resultModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1001]" onClick={() => setResultModal(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFDF9] rounded-[18px] border border-[#E4D8C8] shadow-earth p-8 max-w-sm w-full mx-4 text-center" onClick={e => e.stopPropagation()}>
              <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 ${resultModal.type === 'success' ? 'bg-[#DFF5E7] text-[#1F7A4F]' : 'bg-[#FCE8E8] text-[#D64545]'}`}>
                {resultModal.type === 'success' ? <Check className="w-6 h-6" /> : <X className="w-6 h-6" />}
              </div>
              <h3 className="text-sm font-bold text-[#243238] uppercase tracking-wider mb-2">{resultModal.type === 'success' ? 'Success' : 'Error'}</h3>
              <p className="text-xs text-[#6F706C] mb-6">{resultModal.message}</p>
              <button onClick={() => setResultModal(null)} className="btn-primary text-xs px-6 py-2.5">Close</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Settings;
